import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Calculate from '@mui/icons-material/Calculate';
import countriesNl from '../data/countries';
import countriesEn from '../data/countries.en';
import { useLanguage } from '../context/LanguageContext';
import { useT } from '../translations';

const CountryDetailPage = () => {
	const { land } = useParams();
	const navigate = useNavigate();
	const { language } = useLanguage();
	const t = useT(language);
	const countries = language === 'en' ? countriesEn : countriesNl;
	const country = countries.find((c) => c.slug === land);

	if (!country) {
		return (
			<Box sx={{ textAlign: 'center', py: 8 }}>
				<Typography variant="h5" gutterBottom>
					{t('countryNotFound')}
				</Typography>
				<Button onClick={() => navigate('/emigreren')} startIcon={<ArrowBack />}>
					{t('backToOverview')}
				</Button>
			</Box>
		);
	}

	const description = country.content.intro.slice(0, 160);
	const jsonLd = {
		'@context': 'https://schema.org',
		'@type': 'Article',
		headline: `${country.name} – emigreren gids`,
		description,
		url: `https://www.visualiseermijnpensioen.nl/emigreren/${country.slug}`,
		inLanguage: language === 'en' ? 'en' : 'nl-NL',
		publisher: {
			'@type': 'Organization',
			name: 'VisualiseermijnPensioen.nl',
			url: 'https://www.visualiseermijnpensioen.nl',
		},
	};

	return (
		<Box sx={{ py: 4 }}>
			<SEOHead
				title={`${country.name} | VisualiseermijnPensioen.nl`}
				description={description}
				canonical={`https://www.visualiseermijnpensioen.nl/emigreren/${country.slug}`}
				lang={language === 'en' ? 'en' : 'nl'}
				jsonLd={jsonLd}
			/>
			{/* Back nav */}
			<Button
				startIcon={<ArrowBack />}
				onClick={() => navigate('/emigreren')}
				sx={{ mb: 3, textTransform: 'none' }}
				color="inherit"
			>
				{t('allDestinations')}
			</Button>

			{/* Hero */}
			<Paper
				elevation={0}
				sx={{
					p: { xs: 3, md: 4 },
					mb: 4,
					borderRadius: 3,
					background: `linear-gradient(135deg, ${country.bgColor} 0%, #fff 100%)`,
					border: '1px solid',
					borderColor: 'grey.200',
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
					<Typography fontSize={64} lineHeight={1}>
						{country.flag}
					</Typography>
					<Box sx={{ flex: 1, minWidth: 200 }}>
						<Typography variant="h3" fontWeight="bold" gutterBottom>
							{country.name}
						</Typography>
						<Typography variant="h6" color="text.secondary" gutterBottom>
							{country.subtitle}
						</Typography>
						<Chip
							label={`${country.taxRate} ${country.taxLabel}`}
							sx={{ bgcolor: country.color, color: 'white', fontWeight: 700, fontSize: 14 }}
						/>
					</Box>
				</Box>

				<Typography variant="body1" sx={{ mt: 3, lineHeight: 1.8 }}>
					{country.content.intro}
				</Typography>
			</Paper>

			{/* Pros / Cons */}
			<Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
				<Paper elevation={0} sx={{ flex: 1, minWidth: 200, p: 3, borderRadius: 3, border: '1px solid', borderColor: 'success.light', bgcolor: '#f0fdf4' }}>
					<Typography variant="subtitle1" fontWeight="bold" color="success.dark" gutterBottom>
						{t('pros')}
					</Typography>
					{country.pros.map((pro) => (
						<Typography key={pro} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
							• {pro}
						</Typography>
					))}
				</Paper>
				<Paper elevation={0} sx={{ flex: 1, minWidth: 200, p: 3, borderRadius: 3, border: '1px solid', borderColor: 'error.light', bgcolor: '#fff5f5' }}>
					<Typography variant="subtitle1" fontWeight="bold" color="error.dark" gutterBottom>
						{t('cons')}
					</Typography>
					{country.cons.map((con) => (
						<Typography key={con} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
							• {con}
						</Typography>
					))}
				</Paper>
			</Box>

			{/* Content sections */}
			{country.content.sections.map((section, i) => (
				<Box key={i} sx={{ mb: 4 }}>
					<Typography variant="h5" fontWeight="bold" gutterBottom>
						{section.title}
					</Typography>
					<Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
						{section.body}
					</Typography>
					{i < country.content.sections.length - 1 && <Divider sx={{ mt: 3 }} />}
				</Box>
			))}

			{/* CTA block */}
			<Paper
				elevation={0}
				sx={{
					p: 3,
					mt: 2,
					mb: 4,
					borderRadius: 3,
					bgcolor: '#eef2ff',
					border: '1px solid',
					borderColor: 'primary.light',
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					alignItems: { md: 'center' },
					gap: 2,
				}}
			>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
						{country.content.cta}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{country.content.advisorText}
					</Typography>
				</Box>
				<Button
					variant="contained"
					startIcon={<Calculate />}
					onClick={() => navigate('/')}
					sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
				>
					{t('calculatePension')}
				</Button>
			</Paper>

			{/* Disclaimer */}
			<Paper elevation={0} sx={{ p: 2.5, bgcolor: 'grey.50', borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}>
				<Typography variant="caption" color="text.secondary">
					{t('countryDisclaimer')}
				</Typography>
			</Paper>
		</Box>
	);
};

export default CountryDetailPage;
